/**
 * General print paper sizes for Printing Toolkit.
 * Maps to QZ profiles on Brother MFC-J6955DW (and any printer staff select).
 */

export const GENERAL_PRINT_SIZES = Object.freeze([
  Object.freeze({
    key: 'A6',
    profileKey: 'generalA6',
    label: 'A6',
    description: 'Cards and small notices',
    widthMm: 105,
    heightMm: 148,
    paperSizeName: 'iso-a6',
    defaultTray: 'Multipurpose tray',
    orientation: 'portrait',
  }),
  Object.freeze({
    key: 'A4',
    profileKey: 'generalA4',
    label: 'A4',
    description: 'Letters, packing extras, PDF packs',
    widthMm: 210,
    heightMm: 297,
    paperSizeName: 'iso-a4',
    defaultTray: 'Tray 1',
    orientation: 'portrait',
  }),
  Object.freeze({
    key: 'A3',
    profileKey: 'generalA3',
    label: 'A3',
    description: 'Large sheets and posters',
    widthMm: 297,
    heightMm: 420,
    paperSizeName: 'iso-a3',
    defaultTray: 'Tray 1',
    orientation: 'portrait',
  }),
  Object.freeze({
    key: 'C5',
    profileKey: 'generalC5',
    label: 'C5',
    description: 'Envelope / C5 stock',
    widthMm: 162,
    heightMm: 229,
    paperSizeName: 'iso-c5',
    defaultTray: 'Multipurpose tray',
    orientation: 'portrait',
  }),
  Object.freeze({
    key: 'C6',
    profileKey: 'generalC6',
    label: 'C6',
    description: 'Envelope / C6 stock',
    widthMm: 114,
    heightMm: 162,
    paperSizeName: 'iso-c6',
    defaultTray: 'Multipurpose tray',
    orientation: 'portrait',
  }),
]);

/** Preferred outer page margin for short letters / notices (mm). */
export const GENERAL_LETTER_MARGIN_MM = 14;
const LETTER_MIN_MARGIN_MM = 8;
const LETTER_TITLE_GAP_PX = 14;
const LETTER_MIN_TITLE_GAP_PX = 4;
const LETTER_MIN_BODY_PX = 5;
const LETTER_MAX_BODY_PX = 16;
const LETTER_DEFAULT_TITLE_PX = 20;
const LETTER_MIN_TITLE_PX = 11;
const LETTER_MAX_LINE_HEIGHT = 1.5;
const LETTER_MIN_LINE_HEIGHT = 1.05;

export function getGeneralPrintSize(sizeKey) {
  return (
    GENERAL_PRINT_SIZES.find((size) => size.key === sizeKey) || GENERAL_PRINT_SIZES[1]
  );
}

function mmToCssPx(mm) {
  return (Number(mm) || 0) * (96 / 25.4);
}

function escapeHtmlText(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Rough font size when DOM measurement is unavailable (tests / non-browser).
 * @param {string} body
 * @param {{ widthMm?: number, heightMm?: number }} [size]
 * @returns {number}
 */
export function estimateGeneralLetterFontSizeFallback(body, size) {
  const chars = String(body || '').replace(/\s+/g, ' ').trim().length;
  const width = Math.max(40, (Number(size?.widthMm) || 210) - GENERAL_LETTER_MARGIN_MM * 2);
  const height = Math.max(40, (Number(size?.heightMm) || 297) - GENERAL_LETTER_MARGIN_MM * 2 - 28);
  const area = width * height;
  const density = chars / Math.max(area, 1);
  if (density <= 0.04) return 14;
  if (density <= 0.07) return 12;
  if (density <= 0.11) return 10;
  if (density <= 0.16) return 8;
  return LETTER_MIN_BODY_PX;
}

/**
 * Measure type + spacing so pasted letter text fits one page without clipping.
 * Shrinks font size, line-height, title gap/size, then margins until content fits.
 *
 * @param {{ title?: string, body?: string, size?: object }} args
 * @returns {{ fontSizePx: number, titleSizePx: number, lineHeight: number, titleGapPx: number, marginMm: number }}
 */
export function measureGeneralLetterFit({
  title = 'London Smoke & Cure',
  body = '',
  size,
} = {}) {
  const widthMm = Number(size?.widthMm) || 210;
  const heightMm = Number(size?.heightMm) || 297;
  let marginMm = GENERAL_LETTER_MARGIN_MM;
  let titleSizePx = heightMm <= 162 ? 14 : LETTER_DEFAULT_TITLE_PX;
  let titleGapPx = LETTER_TITLE_GAP_PX;
  let lineHeight = LETTER_MAX_LINE_HEIGHT;
  let fontSizePx = estimateGeneralLetterFontSizeFallback(body, size);

  if (typeof document === 'undefined' || !document?.body) {
    if (String(body || '').length > 2500) {
      lineHeight = LETTER_MIN_LINE_HEIGHT;
      titleGapPx = LETTER_MIN_TITLE_GAP_PX;
      marginMm = LETTER_MIN_MARGIN_MM;
      titleSizePx = LETTER_MIN_TITLE_PX;
    }
    return {
      fontSizePx,
      titleSizePx,
      lineHeight,
      titleGapPx,
      marginMm,
    };
  }

  const host = document.createElement('div');
  host.setAttribute('data-general-letter-fit', '1');
  host.style.cssText = [
    'position:fixed',
    'left:-12000px',
    'top:0',
    'visibility:hidden',
    'pointer-events:none',
    'z-index:-1',
  ].join(';');

  const titleEl = document.createElement('h1');
  titleEl.textContent = String(title || 'London Smoke & Cure');

  const bodyEl = document.createElement('div');
  bodyEl.style.cssText = [
    'white-space:pre-wrap',
    'word-break:break-word',
    'font-family:Georgia,"Times New Roman",serif',
    'margin:0',
  ].join(';');
  bodyEl.textContent = String(body || '');

  host.appendChild(titleEl);
  host.appendChild(bodyEl);
  document.body.appendChild(host);

  const applyProbe = () => {
    const contentW = Math.max(1, mmToCssPx(widthMm - marginMm * 2));
    host.style.width = `${contentW}px`;
    titleEl.style.cssText = [
      'margin:0',
      `margin-bottom:${titleGapPx}px`,
      `font-size:${titleSizePx}px`,
      'font-family:Georgia,"Times New Roman",serif',
      'font-weight:700',
      'line-height:1.15',
    ].join(';');
    bodyEl.style.fontSize = `${fontSizePx}px`;
    bodyEl.style.lineHeight = String(lineHeight);
  };

  const pageHeightPx = () => mmToCssPx(heightMm);
  const contentFits = () => {
    const used = titleEl.offsetHeight + titleGapPx + bodyEl.offsetHeight;
    const available = pageHeightPx() - mmToCssPx(marginMm * 2);
    return used <= available + 0.5;
  };

  try {
    applyProbe();

    // 1) Largest body font that fits at comfortable spacing
    let lo = LETTER_MIN_BODY_PX;
    let hi = LETTER_MAX_BODY_PX;
    let bestFont = LETTER_MIN_BODY_PX;
    while (lo <= hi + 0.01) {
      const mid = Math.round(((lo + hi) / 2) * 2) / 2;
      fontSizePx = mid;
      applyProbe();
      if (contentFits()) {
        bestFont = mid;
        lo = mid + 0.5;
      } else {
        hi = mid - 0.5;
      }
    }
    fontSizePx = bestFont;
    applyProbe();

    // 2) Tighten line-height
    while (!contentFits() && lineHeight > LETTER_MIN_LINE_HEIGHT + 0.001) {
      lineHeight = Math.max(
        LETTER_MIN_LINE_HEIGHT,
        Math.round((lineHeight - 0.05) * 100) / 100,
      );
      applyProbe();
    }

    // 3) Tighten title spacing / size
    while (!contentFits() && titleGapPx > LETTER_MIN_TITLE_GAP_PX) {
      titleGapPx = Math.max(LETTER_MIN_TITLE_GAP_PX, titleGapPx - 2);
      applyProbe();
    }
    while (!contentFits() && titleSizePx > LETTER_MIN_TITLE_PX) {
      titleSizePx = Math.max(LETTER_MIN_TITLE_PX, titleSizePx - 1);
      applyProbe();
    }

    // 4) Reduce page margins (gains width + height)
    while (!contentFits() && marginMm > LETTER_MIN_MARGIN_MM) {
      marginMm = Math.max(LETTER_MIN_MARGIN_MM, marginMm - 1);
      applyProbe();
    }

    // 5) Keep shrinking body type until it fits (never clip)
    while (!contentFits() && fontSizePx > LETTER_MIN_BODY_PX) {
      fontSizePx = Math.max(LETTER_MIN_BODY_PX, Math.round((fontSizePx - 0.5) * 2) / 2);
      applyProbe();
    }
    // Absolute last resort: go below the usual floor rather than hide text
    while (!contentFits() && fontSizePx > 3.5) {
      fontSizePx = Math.max(3.5, Math.round((fontSizePx - 0.25) * 4) / 4);
      applyProbe();
    }

    return {
      fontSizePx,
      titleSizePx,
      lineHeight: Math.round(lineHeight * 100) / 100,
      titleGapPx,
      marginMm,
    };
  } finally {
    host.remove();
  }
}

export function buildGeneralCalibrationHtml(size) {
  const title = String(size?.label || 'Print');
  const width = Number(size?.widthMm) || 210;
  const height = Number(size?.heightMm) || 297;
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>London Smoke & Cure · ${title} calibration</title>
  <style>
    @page { size: ${width}mm ${height}mm; margin: 10mm; }
    html, body { margin: 0; padding: 0; font-family: Georgia, "Times New Roman", serif; color: #1f2933; }
    .sheet {
      box-sizing: border-box;
      width: ${width}mm;
      min-height: ${height}mm;
      padding: 12mm;
      border: 1px dashed #b99040;
    }
    h1 { margin: 0 0 8px; font-size: 22px; }
    p { margin: 0 0 8px; font-size: 13px; line-height: 1.4; }
    .meta { color: #5c6770; font-size: 12px; }
  </style>
</head>
<body>
  <div class="sheet">
    <h1>London Smoke & Cure</h1>
    <p><strong>${title} calibration page</strong></p>
    <p class="meta">${width} × ${height} mm · ${size?.paperSizeName || ''} · ${size?.defaultTray || ''}</p>
    <p class="meta">Use this sheet to confirm tray, orientation, and margins before a mixed queue run.</p>
  </div>
</body>
</html>`;
}

/**
 * Short letter / notice HTML fitted to a single page.
 * Prefer `buildFittedGeneralLetterHtml` so pasted text is measured first.
 * Never clips or hides body text: type and spacing shrink until content fits.
 *
 * @param {{ title?: string, body?: string, size?: object, fontSizePx?: number, titleSizePx?: number, lineHeight?: number, titleGapPx?: number, marginMm?: number }} args
 */
export function buildGeneralLetterHtml({
  title = 'London Smoke & Cure',
  body = '',
  size,
  fontSizePx,
  titleSizePx,
  lineHeight,
  titleGapPx,
  marginMm,
} = {}) {
  const width = Number(size?.widthMm) || 210;
  const height = Number(size?.heightMm) || 297;
  const fit =
    fontSizePx != null
      ? {
          fontSizePx: Number(fontSizePx) || LETTER_MAX_BODY_PX,
          titleSizePx: Number(titleSizePx) || LETTER_DEFAULT_TITLE_PX,
          lineHeight: Number(lineHeight) > 0 ? Number(lineHeight) : LETTER_MAX_LINE_HEIGHT,
          titleGapPx: Number(titleGapPx) >= 0 ? Number(titleGapPx) : LETTER_TITLE_GAP_PX,
          marginMm:
            Number(marginMm) > 0 ? Number(marginMm) : GENERAL_LETTER_MARGIN_MM,
        }
      : measureGeneralLetterFit({ title, body, size });

  const safeTitle = escapeHtmlText(title).replace(/"/g, '&quot;');
  const safeBody = escapeHtmlText(body).replace(/\n/g, '<br />');
  const bodyFont = Math.max(3.5, fit.fontSizePx || LETTER_MAX_BODY_PX);
  const headingFont = Math.max(LETTER_MIN_TITLE_PX, fit.titleSizePx || LETTER_DEFAULT_TITLE_PX);
  const gap = Math.max(LETTER_MIN_TITLE_GAP_PX, fit.titleGapPx ?? LETTER_TITLE_GAP_PX);
  const lh = Math.max(
    LETTER_MIN_LINE_HEIGHT,
    Math.min(LETTER_MAX_LINE_HEIGHT, fit.lineHeight || LETTER_MAX_LINE_HEIGHT),
  );
  const margin = Math.max(
    LETTER_MIN_MARGIN_MM,
    Number(fit.marginMm) > 0 ? Number(fit.marginMm) : GENERAL_LETTER_MARGIN_MM,
  );

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${safeTitle}</title>
  <style>
    @page { size: ${width}mm ${height}mm; margin: 0; }
    html, body {
      margin: 0;
      padding: 0;
      width: ${width}mm;
      height: ${height}mm;
      font-family: Georgia, "Times New Roman", serif;
      color: #1f2933;
    }
    .sheet {
      box-sizing: border-box;
      width: ${width}mm;
      height: ${height}mm;
      padding: ${margin}mm;
    }
    h1 {
      margin: 0 0 ${gap}px;
      font-size: ${headingFont}px;
      line-height: 1.15;
    }
    .body {
      margin: 0;
      font-size: ${bodyFont}px;
      line-height: ${lh};
      white-space: pre-wrap;
      word-break: break-word;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <h1>${safeTitle}</h1>
    <div class="body">${safeBody || '&nbsp;'}</div>
  </div>
  <script>
(function () {
  var MIN_BODY = 3.5;
  var MIN_LH = ${LETTER_MIN_LINE_HEIGHT};
  var MIN_GAP = ${LETTER_MIN_TITLE_GAP_PX};
  var MIN_TITLE = ${LETTER_MIN_TITLE_PX};
  var MIN_MARGIN_MM = ${LETTER_MIN_MARGIN_MM};
  function mm(n) { return n + 'mm'; }
  function refit() {
    var sheet = document.querySelector('.sheet');
    var body = document.querySelector('.body');
    var title = document.querySelector('h1');
    if (!sheet || !body) return;
    var pageH = sheet.clientHeight || 0;
    if (!pageH) return;
    var size = parseFloat(body.style.fontSize) || ${bodyFont};
    var lh = parseFloat(body.style.lineHeight) || ${lh};
    var gap = ${gap};
    var titleSize = title ? (parseFloat(title.style.fontSize) || ${headingFont}) : ${headingFont};
    var marginMm = ${margin};

    function apply() {
      sheet.style.padding = mm(marginMm);
      if (title) {
        title.style.fontSize = titleSize + 'px';
        title.style.marginBottom = gap + 'px';
      }
      body.style.fontSize = size + 'px';
      body.style.lineHeight = String(lh);
    }
    function usedHeight() {
      var titleH = title ? title.offsetHeight + gap : 0;
      return titleH + body.offsetHeight;
    }
    function available() {
      var pad = 0;
      try {
        var cs = window.getComputedStyle(sheet);
        pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      } catch (e) {}
      return Math.max(24, pageH - pad);
    }
    function fits() { return usedHeight() <= available() + 0.5; }

    apply();
    while (!fits() && size > MIN_BODY) {
      size = Math.max(MIN_BODY, size - 0.5);
      apply();
    }
    while (!fits() && lh > MIN_LH) {
      lh = Math.max(MIN_LH, Math.round((lh - 0.05) * 100) / 100);
      apply();
    }
    while (!fits() && gap > MIN_GAP) {
      gap = Math.max(MIN_GAP, gap - 2);
      apply();
    }
    while (!fits() && titleSize > MIN_TITLE) {
      titleSize = Math.max(MIN_TITLE, titleSize - 1);
      apply();
    }
    while (!fits() && marginMm > MIN_MARGIN_MM) {
      marginMm = Math.max(MIN_MARGIN_MM, marginMm - 1);
      apply();
    }
    while (!fits() && size > MIN_BODY) {
      size = Math.max(MIN_BODY, size - 0.25);
      apply();
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refit);
  } else {
    refit();
  }
  window.addEventListener('beforeprint', refit);
})();
  </script>
</body>
</html>`;
}

/**
 * Measure then build a one-page letter/notice (preferred for pasted text).
 * @param {{ title?: string, body?: string, size?: object }} args
 * @returns {Promise<string>}
 */
export async function buildFittedGeneralLetterHtml(args = {}) {
  const fit = measureGeneralLetterFit(args);
  return buildGeneralLetterHtml({
    ...args,
    fontSizePx: fit.fontSizePx,
    titleSizePx: fit.titleSizePx,
    lineHeight: fit.lineHeight,
    titleGapPx: fit.titleGapPx,
    marginMm: fit.marginMm,
  });
}
